import { ref } from 'vue'
import {
  CONTENT_LIBRARY_ITEM_DEFAULT_SORT,
  CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS,
  CONTENT_LIBRARY_ITEM_SORT_OPTIONS
} from '../config/contentLibraryItemFilters'
import { toContentLibraryDate } from '../utils/contentLibraryHelpers'

export const createDefaultContentLibraryItemFilters = () => ({
  recipient: null,
  lawScope: null
})

export const useContentLibraryItemFilters = ({ favoriteItemIds, downloadStatsByItemId, getSortableItemTitle }) => {
  const selectedSortBy = ref(CONTENT_LIBRARY_ITEM_DEFAULT_SORT)
  const selectedFilters = ref(createDefaultContentLibraryItemFilters())

  const getBaseOptionLabel = (option) => {
    return option.baseLabel || option.label
  }

  const matchesSelectedFiltersWithIgnoredKey = (item, ignoredFilterKey = null) => {
    return CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS.every((filterDefinition) => {
      if (filterDefinition.key === ignoredFilterKey) {
        return true
      }

      const selectedValue = selectedFilters.value[filterDefinition.key]

      if (!selectedValue) {
        return true
      }

      return item?.[filterDefinition.key] === selectedValue
    })
  }

  const matchesSelectedFilters = (item) => {
    return matchesSelectedFiltersWithIgnoredKey(item)
  }

  const getDownloadStats = (itemId) => {
    return downloadStatsByItemId?.value?.get(itemId) || null
  }

  const matchesSelectedSort = (item) => {
    if (selectedSortBy.value === 'my-favorites') {
      return favoriteItemIds.value.has(item.id)
    }

    if (selectedSortBy.value === 'my-recently-downloaded' || selectedSortBy.value === 'my-frequently-downloaded') {
      return Boolean(getDownloadStats(item.id))
    }

    return true
  }

  const sortItemsAlphabetically = (items = []) => {
    return [...items].sort((a, b) =>
      getSortableItemTitle(a).localeCompare(getSortableItemTitle(b), undefined, {
        sensitivity: 'base'
      })
    )
  }

  // Feeds both the RTDB item `created_at` (ISO string) and the Firestore
  // download stats `lastDownloadedAt` (Timestamp, or ISO string on docs the
  // migration has not reached yet), so it must accept every shape.
  const getComparableTimestamp = (value) => {
    return toContentLibraryDate(value)?.getTime() ?? 0
  }

  const sortAlphabeticallyByTitle = (items = []) => {
    return [...items].sort((a, b) =>
      getSortableItemTitle(a).localeCompare(getSortableItemTitle(b), undefined, {
        sensitivity: 'base'
      })
    )
  }

  const sortRecentlyAdded = (items = []) => {
    return [...items].sort((a, b) => {
      const timestampDifference = getComparableTimestamp(b.created_at) - getComparableTimestamp(a.created_at)

      if (timestampDifference !== 0) {
        return timestampDifference
      }

      return getSortableItemTitle(a).localeCompare(getSortableItemTitle(b), undefined, {
        sensitivity: 'base'
      })
    })
  }

  const sortRecentlyDownloaded = (items = []) => {
    return [...items].sort((a, b) => {
      const timestampDifference = getComparableTimestamp(getDownloadStats(b.id)?.lastDownloadedAt) - getComparableTimestamp(getDownloadStats(a.id)?.lastDownloadedAt)

      if (timestampDifference !== 0) {
        return timestampDifference
      }

      return getSortableItemTitle(a).localeCompare(getSortableItemTitle(b), undefined, {
        sensitivity: 'base'
      })
    })
  }

  const sortFrequentlyDownloaded = (items = []) => {
    return [...items].sort((a, b) => {
      const downloadCountDifference = Number(getDownloadStats(b.id)?.downloadCount || 0) - Number(getDownloadStats(a.id)?.downloadCount || 0)

      if (downloadCountDifference !== 0) {
        return downloadCountDifference
      }

      const timestampDifference = getComparableTimestamp(getDownloadStats(b.id)?.lastDownloadedAt) - getComparableTimestamp(getDownloadStats(a.id)?.lastDownloadedAt)

      if (timestampDifference !== 0) {
        return timestampDifference
      }

      return getSortableItemTitle(a).localeCompare(getSortableItemTitle(b), undefined, {
        sensitivity: 'base'
      })
    })
  }

  const sortItemsByMode = (items = [], sortMode = selectedSortBy.value) => {
    if (sortMode === 'recently-added') {
      return sortRecentlyAdded(items)
    }

    if (sortMode === 'my-recently-downloaded') {
      return sortRecentlyDownloaded(items)
    }

    if (sortMode === 'my-frequently-downloaded') {
      return sortFrequentlyDownloaded(items)
    }

    return sortAlphabeticallyByTitle(items)
  }

  const sortItemsBySelectedSort = (items = []) => {
    if (selectedSortBy.value === CONTENT_LIBRARY_ITEM_DEFAULT_SORT) {
      return sortItemsAlphabetically(items)
    }

    const matchingItems = items.filter((item) => matchesSelectedSort(item))
    const remainingItems = items.filter((item) => !matchesSelectedSort(item))

    return [
      ...sortItemsByMode(matchingItems),
      ...sortAlphabeticallyByTitle(remainingItems)
    ]
  }

  const flattenCategoryItems = (categories = []) => {
    return categories.flatMap((category) => category.items || [])
  }

  const getFilterDefinitionsWithCounts = (categories = [], options = {}) => {
    const { itemPredicate } = options
    const sourceItems = flattenCategoryItems(categories)

    return CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS.map((filterDefinition) => ({
      ...filterDefinition,
      options: filterDefinition.options.map((option) => {
        const baseLabel = getBaseOptionLabel(option)
        const count = sourceItems.filter((item) => {
          if (itemPredicate && !itemPredicate(item)) {
            return false
          }

          if (!matchesSelectedFiltersWithIgnoredKey(item, filterDefinition.key)) {
            return false
          }

          return item?.[filterDefinition.key] === option.value
        }).length

        return {
          ...option,
          baseLabel,
          count,
          disabled: count === 0,
          label: `${baseLabel} (${count})`
        }
      })
    }))
  }

  const applyItemViewStateToCategories = (categories = []) => {
    return categories
      .map((category) => {
        const sourceItems = category.items || []
        const visibleItems = sourceItems.filter((item) => {
          return matchesSelectedFilters(item)
        })

        return {
          ...category,
          items: sortItemsBySelectedSort(visibleItems)
        }
      })
      .filter((category) => category.items.length > 0)
  }

  return {
    filterDefinitions: CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS,
    sortByOptions: CONTENT_LIBRARY_ITEM_SORT_OPTIONS,
    selectedFilters,
    selectedSortBy,
    getFilterDefinitionsWithCounts,
    applyItemViewStateToCategories,
    sortItemsAlphabetically
  }
}
